/**
 * Warranty Renewal Invoice PDF Generator
 * Reuses jsPDF + autoTable (same libs as invoicePDF.js).
 * Generates a "TAX INVOICE – WARRANTY RENEWAL" PDF.
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Generate and download warranty renewal invoice as PDF
 * @param {Object} invoiceData  - { invoice, order }
 * @param {Object} invoiceData.invoice
 * @param {string} invoiceData.invoice.invoiceNumber        e.g. "REN-1709456789123"
 * @param {string} invoiceData.invoice.invoiceDate          e.g. "01 March 2026"
 * @param {string} invoiceData.invoice.renewalRefNo         e.g. "REN-0001"
 * @param {string} invoiceData.invoice.originalOrderNo      e.g. "699e84df76ebf8298f028f15"
 * @param {Object} invoiceData.invoice.customer             { name, email, phone, address? }
 * @param {Array}  invoiceData.invoice.items                [{ srNo, description, warranty, quantity, unitPrice, lineTotal }]
 * @param {Object} invoiceData.invoice.pricing              { subtotal, gstPercentage, gstAmount, total }
 * @param {string} invoiceData.invoice.paymentMethod        e.g. "ONLINE"
 * @param {string} invoiceData.invoice.paymentStatus        e.g. "PAID"
 * @param {string} invoiceData.invoice.transactionType      e.g. "WARRANTY RENEWAL"
 * @param {Object} invoiceData.order                        { _id }
 */
export const generateWarrantyInvoicePDF = (invoiceData) => {
  const { invoice, order } = invoiceData;

  const doc = new jsPDF();

  doc.setProperties({
    title: `Warranty Invoice ${invoice.invoiceNumber}`,
    subject: `Warranty Renewal for Order ${order._id}`,
    author: "Lapzop",
    creator: "Lapzop E-commerce",
  });

  // ── Company name (top-left) ──────────────────────────────────────────────
  doc.setFontSize(24);
  doc.setTextColor(31, 81, 255);
  doc.setFont("helvetica", "bold");
  doc.text("Lapzop", 14, 20);

  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "normal");
  doc.text("Your Trusted Laptop Partner", 14, 27);

  // ── Company address (top-right) ──────────────────────────────────────────
  doc.setFontSize(9);
  doc.text("NO 5 AND 6, KRISHNAPPA COMPOUND,", 130, 20);
  doc.text("JARAGANAHALLY, J P NAGAR 7TH PHASE,", 130, 26);
  doc.text("KANAKAPURA MAIN ROAD,", 130, 32);
  doc.text("Bengaluru (Bangalore) Urban,", 130, 38);
  doc.text("Karnataka, 560078", 130, 44);
  doc.text("Phone: +91 9353035776", 130, 50);
  doc.text("Email: btxinwardoutward@gmail.com", 130, 56);
  doc.text("Email: Jplapzop@gmail.com", 130, 62);

  // ── Invoice heading ──────────────────────────────────────────────────────
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("TAX INVOICE \u2013 WARRANTY RENEWAL", 14, 65);

  // ── Invoice meta (left column) ───────────────────────────────────────────
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Invoice Number: ${invoice.invoiceNumber}`, 14, 75);
  doc.text(`Invoice Date: ${invoice.invoiceDate}`, 14, 81);
  doc.text(`Renewal Ref No: ${invoice.renewalRefNo}`, 14, 87);
  doc.text(`Original Order No: ${invoice.originalOrderNo}`, 14, 93);

  // ── Bill To (right column) ───────────────────────────────────────────────
  let yPos = 75;
  doc.setFont("helvetica", "bold");
  doc.text("Bill To:", 130, yPos);

  yPos += 6;
  doc.setFont("helvetica", "normal");
  doc.text(invoice.customer.name || "Customer", 130, yPos);
  if (invoice.customer.email) {
    yPos += 5;
    doc.text(`Email: ${invoice.customer.email}`, 130, yPos);
  }
  if (invoice.customer.phone) {
    yPos += 5;
    doc.text(`Phone: ${invoice.customer.phone}`, 130, yPos);
  }
  if (invoice.customer.address) {
    const addr = invoice.customer.address;
    yPos += 5;
    if (addr.addressLine1) doc.text(addr.addressLine1, 130, yPos);
    if (addr.city || addr.state) {
      yPos += 5;
      doc.text(
        `${addr.city || ""}, ${addr.state || ""} ${addr.postalCode || ""}`,
        130,
        yPos,
      );
    }
    if (addr.country) {
      yPos += 5;
      doc.text(addr.country, 130, yPos);
    }
  }

  // ── Separator line ───────────────────────────────────────────────────────
  const lineY = Math.max(88, yPos + 8);
  doc.setLineWidth(0.5);
  doc.line(14, lineY, 196, lineY);

  // ── Renewal items table ──────────────────────────────────────────────────
  const tableStartY = lineY + 7;

  const tableData = invoice.items.map((item) => {
    const itemTotalWithTax = invoice.pricing.subtotal
      ? item.lineTotal +
      (item.lineTotal / invoice.pricing.subtotal) * invoice.pricing.gstAmount
      : item.lineTotal;

    return [
      item.srNo,
      item.description,
      item.warranty || "-",
      `Rs. ${Number(itemTotalWithTax).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
    ];
  });

  if (typeof autoTable !== "function") {
    throw new Error(
      "autoTable function not available. Please check jspdf-autotable installation.",
    );
  }

  autoTable(doc, {
    startY: tableStartY,
    head: [["Sr No", "Product Name", "Renewal Period", "Total"]],
    body: tableData,
    theme: "striped",
    headStyles: {
      fillColor: [31, 81, 255],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9,
    },
    bodyStyles: { fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 12, halign: "center" },
      1: { cellWidth: 114, halign: "left" },
      2: { cellWidth: 30, halign: "center" },
      3: { cellWidth: 32, halign: "right" },
    },
    margin: { left: 14, right: 14 },
    styles: { overflow: "linebreak", cellPadding: 3 },
    didParseCell: function (data) {
      if (data.column.index === 1) {
        data.cell.styles.cellWidth = 114;
      }
    },
  });

  const finalY =
    doc.lastAutoTable && doc.lastAutoTable.finalY
      ? doc.lastAutoTable.finalY + 10
      : tableStartY + tableData.length * 8 + 20;

  // ── Pricing summary ──────────────────────────────────────────────────────
  let summaryY = finalY;

  // ── Payment info ─────────────────────────────────────────────────────────
  summaryY += 15;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Payment Method: ${invoice.paymentMethod}`, 14, summaryY);
  summaryY += 6;
  doc.text(`Payment Status: ${invoice.paymentStatus}`, 14, summaryY);
  summaryY += 6;
  doc.text(`Transaction Type: ${invoice.transactionType}`, 14, summaryY);

  // ── Terms ────────────────────────────────────────────────────────────────
  summaryY += 15;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Terms & Conditions:", 14, summaryY);

  summaryY += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(
    "1. Warranty renewal is non-refundable once activated.",
    14,
    summaryY,
  );
  summaryY += 5;
  doc.text("2. Subject to Bangalore jurisdiction only.", 14, summaryY);
  summaryY += 5;
  doc.text(
    "3. Extended warranty covers as per original warranty terms.",
    14,
    summaryY,
  );
  summaryY += 5;
  doc.text(
    "4. Invoice generated is computer-generated and does not require signature.",
    14,
    summaryY,
  );

  // ── Footer ───────────────────────────────────────────────────────────────
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text(
    "Thank you for renewing your warranty with Lapzop!",
    pageWidth / 2,
    pageHeight - 20,
    { align: "center" },
  );
  doc.text(
    "This is a computer-generated invoice.",
    pageWidth / 2,
    pageHeight - 15,
    { align: "center" },
  );

  doc.save(
    `WarrantyRenewal_${invoice.renewalRefNo}_${invoice.invoiceNumber}.pdf`,
  );
};
